import { FALLBACK_UI_LOCALE, resolveLocale, type ResolvedLocale, type UiLocaleId } from '@visious/core';
import { createFormatters, type Formatters } from '../format/index.ts';
import { applyDocumentLanguage, systemLocales } from '../platform/locale.ts';
import { de } from './de/index.ts';
import { en } from './en/index.ts';

/**
 * **Every word the interface says, in one place** — and the reason it is a place at all.
 *
 * Before this the copy lived in ~40 modules as literals, which was fine for one language and made the
 * second one a search. What made it a *defect* rather than a debt was the pairing: the sentences were
 * English and the numbers beside them were `Intl`'s idea of the browser's locale, so a German reader
 * got "Updated vor 2 Wochen" and "Last agreed jetzt" (`format/index.ts`, `core/i18n/locale.ts`). A
 * catalogue does not fix that on its own — one *locale* fixes it — but it is what makes the fix
 * expressible: the words and the formatters now come from the same resolution, together.
 *
 * ### The type is the English catalogue
 *
 * ```ts
 * export type Strings = Widen<typeof en>;
 * ```
 *
 * so a second language is `const de: Strings = { … }` and **a missing or misspelled key is a type
 * error**, exactly as `Record<ApiErrorKind, …>` makes a missing failure sentence one. (`Widen` is why the
 * `typeof` alone is not enough — see it below; the short version is that `as const` made every sentence
 * its own literal type, so the German catalogue would not have compiled against the English one.) There is no
 * `t('some.key')` and no key strings anywhere: a lookup that can fail at runtime is a lookup that will,
 * on the one screen nobody opened before shipping.
 *
 * ### Read through `text()`, not through an import
 *
 * The active catalogue is module state, set by {@link applyUiLocale} at boot — the same shape as
 * `platform/theme.ts`, where *storing* a theme and *applying* one are deliberately two functions, and
 * the same justification as `platform/instance.ts`'s single platform: this is one instantiation of a
 * boot-time decision, not R10's forbidden singleton. Every model reads it with `text()` rather than
 * importing a frozen object, so the value cannot go stale in a closure; views use {@link useCopy},
 * which subscribes, so changing the language re-renders instead of waiting for a reload.
 */

/*
 * The three imports above carry a full path and an extension, for `api/problem.ts`'s reason: it reaches
 * this module and is run by `node --test`, which resolves neither a directory nor an extensionless
 * specifier. The same applies to everything they reach in turn (`strings/en/*`, `format/`, `platform/
 * locale.ts`), which is why the catalogue's own imports are spelled that way too.
 */

/**
 * `typeof en` with its **string literals widened back to `string`**, and everything else left alone.
 *
 * Every file in `en/` ends in `as const`, which is what makes `retryable: false` a literal and the whole
 * tree readonly — and also made `verified: 'Verified'` the *type* `'Verified'`. So the second language
 * this file was designed for could not be written: `const de: Strings = { … }` failed on its first
 * property with `Type '"Verifiziert"' is not assignable to type '"Verified"'`, and on every one after it.
 * The claim that adding a language is one file was true of the design and untested against a compiler.
 *
 * The three arms before the mapped type are each load-bearing:
 *
 * - **`string` widens.** That is the whole point: a catalogue's words are its own.
 * - **A function keeps its signature exactly.** An interpolated sentence is a function here, and its
 *   parameters are the contract: a translation may reorder the words inside it and may not change what it
 *   is handed.
 * - **`boolean`, `number` and `null` pass through as literals**, which is what pins `retryable`. That flag
 *   is behaviour rather than copy — `ProblemPanel` reads it to decide whether a Try again button appears —
 *   so a translator who flipped it would give German readers a button English readers do not have.
 *   Widening it to `boolean` would have been the easy mistake, and it would have compiled.
 */
type Widen<T> =
  T extends string ? string
  : T extends (...args: infer A) => infer R ? (...args: A) => R
  : T extends boolean | number | null | undefined ? T
  : { readonly [K in keyof T]: Widen<T[K]> };

export type Strings = Widen<typeof en>;

/**
 * One catalogue per language this build has, keyed by the ids core knows about.
 *
 * `Record<UiLocaleId, Strings>` rather than a lookup with a fallback: adding `'de'` to `UI_LOCALES` in
 * core fails *this* build until the catalogue exists, which is the whole point of the union. A locale
 * you can select and cannot render is not a state worth having.
 */
export const CATALOGUES: Readonly<Record<UiLocaleId, Strings>> = { en, de };

/** The words and the numbers for one language, resolved together — which is 4e's entire claim. */
export interface Copy {
  readonly locale: ResolvedLocale;
  readonly text: Strings;
  readonly format: Formatters;
}

function copyFor(locale: ResolvedLocale): Copy {
  return { locale, text: CATALOGUES[locale.ui], format: createFormatters(locale.formatting) };
}

let active: Copy = copyFor({
  ui: FALLBACK_UI_LOCALE,
  formatting: FALLBACK_UI_LOCALE,
  source: 'fallback',
});

const listeners = new Set<() => void>();

/**
 * A language this *page* is to be spoken in, whatever the reader's own setting says — `/embed`'s
 * `hl` parameter, and the only caller.
 *
 * An override rather than a plain {@link applyUiLocale} call, because the setting does not stay
 * still: signing in pulls the account's preferences and applies `uiLocale` from them, which lands
 * after the page has already chosen its language and would quietly undo it. Held here, it wins over
 * every later write instead.
 *
 * `null` gives the setting back its say, which is what leaving an embed means.
 */
let override: UiLocaleId | null = null;

export function applyUiLocaleOverride(wanted: UiLocaleId | null, setting: UiLocaleId | null): void {
  override = wanted;
  applyUiLocale(setting);
}

/**
 * Resolve the language against the system's preferences and make it the one the app speaks — the
 * *apply* half of the pair `platform/theme.ts` argues for. Returns what it resolved, because the
 * settings page shows both the choice and what the choice came out as.
 *
 * Called once at boot and again whenever the setting changes. Idempotent for the same input, and it
 * only notifies when the resolution actually moved: a settings write publishes the whole document, so
 * without that check every unrelated toggle would re-render every screen that renders a word.
 */
export function applyUiLocale(setting: UiLocaleId | null): ResolvedLocale {
  const locale = resolveLocale({ setting: override ?? setting, system: systemLocales() });
  if (
    locale.ui === active.locale.ui &&
    locale.formatting === active.locale.formatting &&
    locale.source === active.locale.source
  ) {
    return active.locale;
  }
  active = copyFor(locale);
  // `<html lang>` moves with the catalogue, for the reason `applyDocumentLanguage` gives: it is what a
  // screen reader picks its phonemes from, so it is part of applying a language rather than a detail of
  // the page shell. Inside the early-return guard, so an unrelated settings write does not touch the DOM.
  applyDocumentLanguage(locale.ui);
  for (const listener of listeners) listener();
  return locale;
}

/** The active catalogue. A function, so a module-level reference cannot freeze one language in. */
export function text(): Strings {
  return active.text;
}

/** The active locale's formatters — the same locale the words came from, by construction. */
export function fmt(): Formatters {
  return active.format;
}

export function activeLocale(): ResolvedLocale {
  return active.locale;
}

/**
 * The store half, for `strings/useCopy.ts` — which is a separate module for a reason that is not
 * tidiness: **this file must not import React.** `api/problem.ts` reads the catalogue and is imported
 * by `core-testkit`'s `problem-copy.test.ts`, which runs under `node --test` with zero dependencies on
 * purpose (R1's spirit, applied to the tests). One `react` import here would end that.
 */
export function subscribeToCopy(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

export function getCopy(): Copy {
  return active;
}
